import {
  BanknoteIcon,
  CalendarDaysIcon,
  SlidersHorizontalIcon,
  UsersIcon,
} from "lucide-react";

import type { FilterDefinitionType } from "@/components/@types";
import type { SharedEntityOption } from "@/app/share/report/sharedReportFilters";
import type { SharedFilterDefinition } from "@/app/share/report/sharedReportTypes";
import { FILTER_OPERATORS_FOR_NUMBERS } from "@/components/organisms/UtilityBar/FilterChip/FilterChip.content";

const ICON_BY_KEY: Record<string, FilterDefinitionType["ReactIcon"]> = {
  dates: CalendarDaysIcon,
  effective_date: CalendarDaysIcon,
  amount: BanknoteIcon,
  entities: UsersIcon,
};

function sharedTypeToFilterType(type: string): FilterDefinitionType["type"] {
  switch (type.toUpperCase()) {
    case "DATE":
      return "date";
    case "ENUM":
      return "enum";
    case "NUMBER":
      return "number";
    case "TEXT":
      return "text";
    default:
      return "text";
  }
}

export function buildSharedEntityFilterDefinition(
  entities: SharedEntityOption[],
): FilterDefinitionType {
  return {
    type: "enum",
    name: "Entities",
    ReactIcon: UsersIcon,
    operators: [
      {
        key: "inList",
        name: "In",
        symbol: "∈",
      },
    ],
    getValueOptions: () => ({
      enum: entities.map((entity) => ({
        key: entity.identifier,
        value: entity.name,
      })),
      getColors: () =>
        Object.fromEntries(
          entities.map((entity) => [entity.name, "var(--foreground)"]),
        ),
    }),
  };
}

export function getSharedReportFilterDefinitions(
  entities: SharedEntityOption[],
  layoutFilterDefinitions: Record<string, FilterDefinitionType>,
): Record<string, FilterDefinitionType> {
  return {
    ...layoutFilterDefinitions,
    ...(entities.length > 1
      ? { entities: buildSharedEntityFilterDefinition(entities) }
      : {}),
  };
}

export function sharedFilterDefinitionsToRecord(
  definitions: SharedFilterDefinition[] | undefined,
): Record<string, FilterDefinitionType> {
  const record: Record<string, FilterDefinitionType> = {};

  for (const definition of definitions ?? []) {
    const type = sharedTypeToFilterType(definition.type);
    const operators =
      definition.operators?.map((operator) => ({
        key: operator.key as any,
        name: operator.name,
        symbol: operator.symbol,
        ...(type === "date" && operator.key === "range"
          ? {
              getInputValue(
                _key: string,
                value: { date1?: string; date2?: string },
              ) {
                return {
                  startDate: value?.date1,
                  endDate: value?.date2,
                };
              },
            }
          : {}),
      })) ?? (type === "number" ? FILTER_OPERATORS_FOR_NUMBERS : []);

    record[definition.key] = {
      type,
      name: definition.name,
      ReactIcon: ICON_BY_KEY[definition.key] ?? SlidersHorizontalIcon,
      operators,
      ...(definition.valueOptions?.length
        ? {
            getValueOptions: () => ({
              enum: definition.valueOptions!.map((option) => ({
                key: option.key,
                value: option.value,
              })),
            }),
          }
        : {}),
    };
  }

  return record;
}
